import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch } from './api';

// Watchlist moving-average alerts.
//
// The checking happens on the backend (backend/maAlerts.js) every minute,
// whether or not the app is open, and a crossing sends one normal-priority
// Bark push. This component is the in-app side: it lists every watchlisted
// stock with the MA it alerts on (10/20/50, changeable here), shows which have
// fired, and re-arms them with Reset. When a new alert fires while the app is
// open it also pops the panel open with a short beep.
const SEEN_KEY = 'bse5-ma-alerts-seen';
const POLL_MS = 60000;
const PERIODS = [10, 20, 50];

const loadSeen = () => {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || '[]')); } catch { return new Set(); }
};
const saveSeen = (seen) => {
  try { localStorage.setItem(SEEN_KEY, JSON.stringify([...seen])); } catch {}
};
const alertId = (row) => `${row.link}|${row.fired_at}`;

const beep = () => {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.6);
    osc.onended = () => ctx.close();
  } catch {}
};

const boxStyle = {
  position: 'fixed', right: 12, bottom: 12, zIndex: 200, width: 330,
  background: '#fff', border: '1px solid #d0d5dd', borderRadius: 8,
  boxShadow: '0 8px 24px rgba(16,24,40,.18)', fontSize: 12, color: '#101828',
};
const pillStyle = (fired) => ({
  position: 'fixed', right: 12, bottom: 12, zIndex: 200, padding: '3px 9px',
  border: `1px solid ${fired ? '#fedf89' : '#d0d5dd'}`, borderRadius: 12,
  background: fired ? '#fffaeb' : '#fff', color: fired ? '#b54708' : '#667085',
  fontSize: 11, cursor: 'pointer', opacity: fired ? 1 : 0.8,
});
const linkButton = {
  border: 'none', background: 'none', color: '#175cd3', cursor: 'pointer', padding: 0, fontSize: 12,
};

const WatchlistAlerts = ({ onOpenStock }) => {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async ({ announce } = {}) => {
    try {
      const response = await apiFetch('/api/ma-alerts');
      const next = response.ok ? await response.json() : [];
      if (!Array.isArray(next)) return;
      setRows(next);
      const seen = loadSeen();
      const fresh = next.filter((row) => row.fired_at && !seen.has(alertId(row)));
      if (fresh.length) {
        fresh.forEach((row) => seen.add(alertId(row)));
        saveSeen(seen);
        if (announce) {
          setOpen(true);
          beep();
        }
      }
    } catch (_) {
      // A missed poll is harmless; the next one retries.
    }
  }, []);

  useEffect(() => {
    // The first load only catches up quietly; later polls announce new alerts.
    load();
    const interval = window.setInterval(() => load({ announce: true }), POLL_MS);
    return () => window.clearInterval(interval);
  }, [load]);

  const post = async (path, body) => {
    try {
      await apiFetch(path, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
    } finally {
      load();
    }
  };

  const firedCount = rows.filter((row) => row.fired_at).length;
  if (rows.length === 0) return null;

  if (!open) {
    return (
      <button type="button" style={pillStyle(firedCount > 0)} onClick={() => setOpen(true)} title="Watchlist moving-average alerts">
        {firedCount > 0 ? `MA alerts: ${firedCount} fired` : 'MA alerts'}
      </button>
    );
  }

  return (
    <div style={boxStyle} role="dialog" aria-label="Watchlist MA alerts">
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', borderBottom: '1px solid #eaecf0', fontWeight: 600 }}>
        <span>Watchlist MA alerts</span>
        <button type="button" style={{ ...linkButton, color: '#667085' }} onClick={() => setOpen(false)} title="Hide">✕</button>
      </div>
      <div style={{ maxHeight: 300, overflowY: 'auto' }}>
        {rows.map((row) => (
          <div key={row.link} style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '5px 10px', borderBottom: '1px solid #f2f4f7', background: row.fired_at ? '#fffaeb' : 'transparent' }}>
            <button type="button" style={{ ...linkButton, fontWeight: 600, flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              onClick={() => onOpenStock && onOpenStock(row.link)}>{row.name || row.link}</button>
            <select value={row.ma_period} onChange={(e) => post('/api/ma-alerts/period', { link: row.link, period: Number(e.target.value) })}
              title="Moving average this stock alerts on" style={{ fontSize: 11 }}>
              {PERIODS.map((p) => <option key={p} value={p}>{p}MA</option>)}
            </select>
            {row.fired_at ? (
              <>
                <span style={{ color: row.direction === 'below' ? '#d92d20' : '#039855', whiteSpace: 'nowrap' }}
                  title={`Fired ${new Date(row.fired_at).toLocaleString()}: ${row.fired_close} vs ${row.fired_ma}`}>
                  {row.direction === 'below' ? '▼ below' : '▲ above'}
                </span>
                <button type="button" style={linkButton} onClick={() => post('/api/ma-alerts/reset', { link: row.link })}>Reset</button>
              </>
            ) : (
              <span style={{ color: '#667085', whiteSpace: 'nowrap' }}
                title={row.ma == null ? 'Waiting for the first reading' : `${row.last_close} vs ${row.ma_period}MA ${row.ma}`}>
                {row.last_below == null ? 'arming' : `armed (${row.last_below ? 'below' : 'above'})`}
              </span>
            )}
          </div>
        ))}
      </div>
      {firedCount > 0 && (
        <div style={{ padding: '6px 10px', textAlign: 'right' }}>
          <button type="button" style={linkButton} onClick={() => post('/api/ma-alerts/reset', {})}>Reset all</button>
        </div>
      )}
    </div>
  );
};

export default WatchlistAlerts;
